import { api } from './instance'
import type { HealthCheck, HealthCheckResult } from '../types'

export interface CreateHealthCheckRequest {
  name: string
  target: string
  type?: 'Http' | 'Tcp' | 'Icmp'
  description?: string
  isEnabled?: boolean
  httpMethod?: string
  expectedStatusCodes?: number[]
  httpHeaders?: string
  httpBody?: string
  httpContentType?: string
  expectedResponseContent?: string
  followRedirects?: boolean
  validateSsl?: boolean
  sslExpiryWarningDays?: number
  intervalSeconds?: number
  timeoutMs?: number
  failureThreshold?: number
  successThreshold?: number
  notificationChannelIds?: string[]
  notifyOnFailure?: boolean
  notifyOnRecovery?: boolean
  notifyOnSslExpiry?: boolean
  tags?: string[]
  application?: string
  environment?: string
  resultsRetentionDays?: number
  isShared?: boolean
}

export interface UpdateHealthCheckRequest {
  name?: string
  target?: string
  type?: 'Http' | 'Tcp' | 'Icmp'
  description?: string
  isEnabled?: boolean
  httpMethod?: string
  expectedStatusCodes?: number[]
  httpHeaders?: string
  httpBody?: string
  httpContentType?: string
  expectedResponseContent?: string
  followRedirects?: boolean
  validateSsl?: boolean
  sslExpiryWarningDays?: number
  intervalSeconds?: number
  timeoutMs?: number
  failureThreshold?: number
  successThreshold?: number
  notificationChannelIds?: string[]
  notifyOnFailure?: boolean
  notifyOnRecovery?: boolean
  notifyOnSslExpiry?: boolean
  tags?: string[]
  application?: string
  environment?: string
  resultsRetentionDays?: number
  isShared?: boolean
}

export interface HealthCheckSummary {
  total: number
  up: number
  down: number
  degraded: number
  paused: number
  unknown: number
}

export interface HealthCheckStatistics {
  checkId: string
  uptime24h: number
  uptime7d: number
  uptime30d: number
  avgResponseTime24h: number
  totalChecks24h: number
  failedChecks24h: number
}

export const healthChecksApi = {
  getAll: async (): Promise<HealthCheck[]> => {
    const { data } = await api.get('/health-checks')
    return data
  },

  getById: async (id: string): Promise<HealthCheck> => {
    const { data } = await api.get(`/health-checks/${id}`)
    return data
  },

  create: async (check: CreateHealthCheckRequest): Promise<HealthCheck> => {
    const { data } = await api.post('/health-checks', check)
    return data
  },

  update: async (id: string, updates: UpdateHealthCheckRequest): Promise<HealthCheck> => {
    const { data } = await api.put(`/health-checks/${id}`, updates)
    return data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/health-checks/${id}`)
  },

  runNow: async (id: string): Promise<HealthCheckResult> => {
    const { data } = await api.post(`/health-checks/${id}/run`)
    return data
  },

  toggle: async (id: string, isEnabled: boolean): Promise<HealthCheck> => {
    const { data } = await api.post(`/health-checks/${id}/toggle`, { isEnabled })
    return data
  },

  getResults: async (id: string, timeRange?: string, limit?: number): Promise<HealthCheckResult[]> => {
    const params = new URLSearchParams()
    if (timeRange) params.set('timeRange', timeRange)
    if (limit) params.set('limit', limit.toString())
    const { data } = await api.get(`/health-checks/${id}/results?${params.toString()}`)
    return data
  },

  getStatistics: async (id: string): Promise<HealthCheckStatistics> => {
    const { data } = await api.get(`/health-checks/${id}/statistics`)
    return data
  },

  getSummary: async (): Promise<HealthCheckSummary> => {
    const { data } = await api.get('/health-checks/summary')
    return data
  },
}
